<template>
  <div class="zoya-dialog flex flex-col bg-cream" style="height: calc(100dvh - var(--safe-top));">

    <!-- ═══ Таб-бар: fixed 72px, blur — как в Дневнике и Кулинарии. Вкладки
         «Чат», «память», «план», «настройки» ═══ -->
    <div class="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 flex items-end"
         style="height: calc(72px + var(--safe-top)); background: rgba(235, 225, 210, 0.6); -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);">
      <button
        v-for="(tab, idx) in tabs"
        :key="tab.key"
        type="button"
        :aria-pressed="activeTab === tab.key"
        :data-guide="'zoya-tab-' + tab.key"
        @click="activeTab = tab.key"
        class="flex-1 flex flex-col items-center justify-end transition-colors relative"
        :class="{
          'tab-active': activeTab === tab.key,
          'tab-first': idx === 0,
          'tab-last': idx === tabs.length - 1,
        }"
        :style="[
          activeTab === tab.key
            ? 'background: rgba(250, 243, 232, 0.75); z-index: 2;'
            : 'background: transparent; z-index: 1;',
          'height: 72px; padding-bottom: 12px;',
          idx === 0 ? 'border-radius: 0 14px 0 0;'
            : idx === tabs.length - 1 ? 'border-radius: 14px 0 0 0;'
            : 'border-radius: 14px 14px 0 0;'
        ]"
      >
        <!-- Вкладка чата — аватар Зои без подписи; остальные — иконка 26 px как в дневнике -->
        <img v-if="tab.avatar" src="/Zoya_avatar_2_small.jpg" alt="Чат с Зоей" class="w-9 h-9 rounded-full object-cover mb-1" :style="{ opacity: activeTab === tab.key ? 1 : 0.6 }" />
        <div v-else class="zoya-tab-icon flex-1 flex items-end pb-1" style="color: #41291A" aria-hidden="true" v-html="tab.svg" />
        <span style="font-family: Inter, sans-serif; font-weight: 400; font-size: var(--fs-12); line-height: 1;"
              :style="{ color: activeTab === tab.key ? '#41291A' : 'rgba(65,41,26,0.6)' }" v-if="tab.label">{{ tab.label }}</span>
      </button>
    </div>

    <!-- Messages area -->
    <div
      v-show="activeTab === 'chat'"
      ref="scrollContainer"
      class="flex-1 min-h-0 overflow-y-auto px-4"
      :style="{ paddingTop: '80px', paddingBottom: (inputBarHeight + keyboardHeight + 24) + 'px' }"
      @scroll="onScroll"
    >
      <!-- Blur fade top -->
      <div class="pointer-events-none fixed top-[calc(72px+var(--safe-top))] left-1/2 -translate-x-1/2 w-full max-w-[430px] h-6 z-40"
           style="background: linear-gradient(to bottom, rgba(250,243,232,1), rgba(250,243,232,0));" />

      <!-- Loading spinner top -->
      <div v-if="loadingOlder" class="flex justify-center py-3">
        <div class="w-5 h-5 border-2 border-[#C4B8A8] border-t-transparent rounded-full animate-spin" />
      </div>

      <!-- AI disclaimer -->
      <div v-if="!loadingOlder && messages.length > 0" class="text-center py-3 px-6">
        <p class="text-12 text-ink/70 leading-snug">
          Зоя — ИИ-помощница. Ответы могут содержать неточности и не заменяют консультацию специалиста.
        </p>
      </div>

      <!-- Messages -->
      <div v-for="(msg, idx) in messages" :key="msg.id" class="mb-3">
        <!-- Date separator -->
        <div v-if="showDateSep(idx)" class="text-center my-4">
          <span class="text-12 text-ink/70 bg-cream px-3 py-1 rounded-full">
            {{ formatDateSep(msg.created_at) }}
          </span>
        </div>

        <!-- Zoya bubble (left) -->
        <div v-if="msg.role === 'zoya'" class="flex justify-start">
          <div class="max-w-[85%]">
            <div class="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm zoya-bubble" v-html="formatZoyaMessage(msg.content)">
            </div>
            <div v-if="msg.metadata?.type==='reminder'" class="flex flex-wrap gap-2 mt-2">
              <button class="bg-cream text-ink rounded-xl px-4 py-2 text-14 font-medium border border-line" @click="performReminder(msg.metadata.reminder_id)">Открыть</button>
              <button class="bg-cream text-ink rounded-xl px-4 py-2 text-14 font-medium border border-line" @click="performReminder(msg.metadata.reminder_id,'skip')">Не сейчас</button>
            </div>
            <!-- Onboarding buttons -->
            <div v-if="msg.metadata?.buttons && idx === messages.length - 1" class="flex flex-wrap gap-2 mt-2">
              <button
                v-for="btn in msg.metadata.buttons"
                :key="btn.label"
                @click="sendAction(btn.action || btn.label, btn.label)"
                :disabled="sending"
                class="bg-cream text-ink rounded-xl px-4 py-2 text-14 font-medium border border-line active:bg-line disabled:opacity-50 transition-colors"
              >
                {{ btn.label }}
              </button>
            </div>
            <!-- Кнопка "Добавить в дневник" -->
            <div v-if="msg.metadata?.add_food_items && idx === messages.length - 1" class="mt-2">
              <button
                @click="openAddFood(msg.metadata.add_food_items)"
                class="bg-ink text-cream rounded-xl px-4 py-2.5 text-14 font-semibold active:opacity-80 transition-opacity"
              >
                Добавить в дневник
              </button>
            </div>
            <!-- Память: что Зоя запомнила из этого ответа (lib/memory.js), с крестиком -->
            <div v-if="msg.metadata?.remembered?.length" class="mt-2 flex flex-col gap-1">
              <div v-for="f in msg.metadata.remembered" :key="f.id" class="flex items-center gap-2 bg-white border border-line rounded-xl px-3 py-1.5 text-13 text-ink">
                <span class="text-ink/60 shrink-0">Запомнила:</span>
                <span class="flex-1 min-w-0 leading-snug">{{ f.text }}</span>
                <button type="button" class="shrink-0 w-6 h-6 flex items-center justify-center text-ink/60" aria-label="Удалить из памяти" @click="forgetFact(msg, f)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <p v-if="msg.metadata?.forgotten" class="text-12 text-ink/60 mt-1 ml-1">Убрала из памяти: {{ msg.metadata.forgotten }}</p>
            <p class="text-11 text-ink/70 mt-1 ml-1">{{ formatTime(msg.created_at) }}</p>
          </div>
        </div>

        <!-- User bubble (right) -->
        <div v-else-if="msg.role === 'user'" class="flex justify-end">
          <div class="max-w-[85%]">
            <div class="bg-ink rounded-2xl rounded-tr-sm px-4 py-3">
              <p class="text-15 text-cream leading-relaxed whitespace-pre-wrap">{{ msg.content }}</p>
            </div>
            <p class="text-11 text-ink/70 mt-1 mr-1 text-right">{{ formatTime(msg.created_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Typing indicator -->
      <div v-if="sending" class="flex justify-start mb-3">
        <div class="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <div class="flex gap-1 items-center h-5">
            <span class="w-2 h-2 bg-[#C4B8A8] rounded-full animate-bounce" style="animation-delay: 0ms;" />
            <span class="w-2 h-2 bg-[#C4B8A8] rounded-full animate-bounce" style="animation-delay: 150ms;" />
            <span class="w-2 h-2 bg-[#C4B8A8] rounded-full animate-bounce" style="animation-delay: 300ms;" />
          </div>
        </div>
      </div>

    </div>

    <!-- Память, план и общие настройки Зои -->
    <div v-if="activeTab !== 'chat'" class="flex-1 min-h-0 overflow-y-auto">
      <MemoryTab v-if="activeTab === 'memory'" />
      <PlanTab v-else-if="activeTab === 'plan'" @ask="askZoya" />
      <ZoyaSettings v-else />
    </div>

    <VoiceRecordSheet v-if="showVoiceSheet" context="chat" @close="showVoiceSheet = false" @recorded="onVoiceRecorded" />

    <!-- PhotoRecognizeDialog для добавления еды из чата -->
    <PhotoRecognizeDialog
      v-if="showTextRecognize"
      mode="text"
      :items="textRecognizeItems"
      @close="showTextRecognize = false"
      @added="onFoodAdded"
    />

    <!-- Общая оболочка поля и кнопок; затухание всегда прилегает к её верхнему краю. -->
    <div ref="inputBar" v-show="activeTab === 'chat'"
         class="fixed inset-x-0 mx-auto w-full max-w-[430px] z-50 bg-cream px-3 pt-2"
         :style="{ bottom: keyboardHeight + 'px', paddingBottom: keyboardVisible ? '8px' : 'calc(80px + var(--nav-inset))' }">
      <div class="chat-bottom-fade pointer-events-none absolute inset-x-0 bottom-full h-6" aria-hidden="true" />
      <p v-if="voiceError" role="alert" class="text-13 text-red-600 px-2 pb-2">{{ voiceError }}</p>
      <p v-if="transcribing" role="status" class="text-13 text-ink/70 px-2 pb-2">Распознаю речь…</p>
      <div class="input-shell chat-composer flex items-end gap-1 p-1">
        <textarea
          ref="inputEl"
          v-model="inputText"
          @input="autoResize"
          @focus="onInputFocus"
          @keydown.enter.exact.prevent="onComposerEnter"
          :placeholder="ty ? 'Спроси Зою' : 'Спросите Зою'"
          aria-label="Сообщение Зое"
          rows="1"
          class="flex-1 min-w-0 px-3 py-3 resize-none"
          style="max-height: 120px; min-height: 48px;"
          :readonly="sending || transcribing"
        />
        <button v-if="voiceSupported" type="button" @click="openVoice"
                :disabled="sending || transcribing || voiceQuotaChecking"
                aria-label="Голосовой ввод"
                class="w-11 h-11 mb-0.5 flex items-center justify-center shrink-0 rounded-full text-ink disabled:opacity-40 active:bg-cream transition-colors">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" />
          </svg>
        </button>
        <button type="button" @click="sendMessage"
                :disabled="!inputText.trim() || sending || transcribing"
                aria-label="Отправить сообщение"
                class="w-11 h-11 mb-0.5 bg-ink rounded-full flex items-center justify-center shrink-0 disabled:opacity-40 active:opacity-80 transition-opacity">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FAF3E8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { markUnsaved, clearUnsaved } from '../services/unsaved.js'
import { useRouter, useRoute } from 'vue-router'
import { perform as performReminder } from '../services/reminders.js'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import { isAiAccessError } from '../services/aiAccess.js'
import PhotoRecognizeDialog from '../components/PhotoRecognizeDialog.vue'
import MemoryTab from '../components/zoya/MemoryTab.vue'
import PlanTab from '../components/zoya/PlanTab.vue'
import VoiceRecordSheet from '../components/VoiceRecordSheet.vue'
import { track } from '../analytics.js'

const ZoyaSettings = defineAsyncComponent(() => import('./ZoyaSettings.vue'))
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const ty = computed(() => authStore.user?.address_form !== 'вы')

// Вкладки экрана Зои: чат (по умолчанию), память, план, настройки
const contentTab = ref('chat')
const activeTab = computed({
  get: () => route.path === '/zoya/settings' ? 'settings' : contentTab.value,
  set(tab) {
    if (tab === 'settings') {
      if (route.path !== '/zoya/settings') router.push('/zoya/settings')
    } else {
      contentTab.value = tab
      if (route.path === '/zoya/settings') router.push('/zoya')
    }
  },
})
const tabs = [
  // Чат — аватар Зои вместо иконки и подписи (решение владельца 26.10.2026)
  { key: 'chat', label: '', avatar: true },
  { key: 'memory', label: 'память', svg: '<svg width="26" height="26" viewBox="0 0 14 13" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path vector-effect="non-scaling-stroke" d="M4.48318 0.5C5.79769 0.5 6.86332 1.56563 6.86332 2.88014L6.86326 9.35057C6.86326 10.855 5.64366 12.0746 4.13921 12.0746C2.63475 12.0746 1.41515 10.855 1.41515 9.35057C1.41515 9.27347 1.41836 9.19717 1.42464 9.12167C0.857702 8.51262 0.5 7.32128 0.5 6.32726C0.5 4.97277 1.16416 3.65582 2.11687 3.13815C2.10773 3.0534 2.10304 2.96732 2.10304 2.88014C2.10304 1.56563 3.16867 0.5 4.48318 0.5Z"/><path vector-effect="non-scaling-stroke" d="M3.37465 4.75586C3.02517 4.67618 2.23323 4.15682 2.11374 3.14429"/><path vector-effect="non-scaling-stroke" d="M4.83362 7.42656C6.26113 7.26723 6.82682 5.76073 6.8636 5.14795"/><path vector-effect="non-scaling-stroke" d="M2.15308 7.6936C1.79752 7.97434 1.51403 8.61058 1.42218 9.12026"/><path vector-effect="non-scaling-stroke" d="M9.24376 0.5C7.92924 0.5 6.86362 1.56563 6.86362 2.88014L6.86368 9.35057C6.86368 10.855 8.08328 12.0746 9.58773 12.0746C11.0922 12.0746 12.3118 10.855 12.3118 9.35057C12.3118 9.27347 12.3086 9.19717 12.3023 9.12167C12.8692 8.51262 13.2269 7.32128 13.2269 6.32726C13.2269 4.97277 12.5628 3.65582 11.6101 3.13815C11.6192 3.0534 11.6239 2.96732 11.6239 2.88014C11.6239 1.56563 10.5583 0.5 9.24376 0.5Z"/><path vector-effect="non-scaling-stroke" d="M10.3523 4.75586C10.7018 4.67618 11.4937 4.15682 11.6132 3.14429"/><path vector-effect="non-scaling-stroke" d="M8.89329 7.42656C7.46579 7.26723 6.90009 5.76073 6.86331 5.14795"/><path vector-effect="non-scaling-stroke" d="M11.5739 7.6936C11.9294 7.97434 12.2129 8.61058 12.3048 9.12026"/></svg>' },
  { key: 'plan', label: 'план', svg: '<svg width="26" height="26" viewBox="0 0 12 14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path vector-effect="non-scaling-stroke" d="M7.25 0.5H4.25C3.69772 0.5 3.25 0.947715 3.25 1.5V2C3.25 2.55228 3.69772 3 4.25 3H7.25C7.80228 3 8.25 2.55228 8.25 2V1.5C8.25 0.947715 7.80228 0.5 7.25 0.5Z"/><path vector-effect="non-scaling-stroke" d="M8.5 1.5H10C10.2652 1.5 10.5196 1.60536 10.7071 1.79289C10.8946 1.98043 11 2.23478 11 2.5V12.5C11 12.7652 10.8946 13.0196 10.7071 13.2071C10.5196 13.3946 10.2652 13.5 10 13.5H1.5C1.23478 13.5 0.98043 13.3946 0.79289 13.2071C0.60536 13.0196 0.5 12.7652 0.5 12.5V2.5C0.5 2.23478 0.60536 1.98043 0.79289 1.79289C0.98043 1.60536 1.23478 1.5 1.5 1.5H3"/><path vector-effect="non-scaling-stroke" d="M3.75 9L5.25 10L8.25 6"/></svg>' },
  { key: 'settings', label: 'настройки', svg: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33 1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82 1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>' },
]

const messages = ref([])
const inputText = ref(sessionStorage.getItem('zoya-chat-draft') || '')
// Набранное, но не отправленное сообщение — незаконченный ввод: автообновление
// приложения ждёт, пока поле опустеет (services/unsaved.js)
const chatUnsavedKey = Symbol('ZoyaDialog')
// Голосовая запись и расшифровка тоже защищены от автообновления приложения.
onUnmounted(() => { clearUnsaved(chatUnsavedKey) })
watch(inputText, (v) => {
  if (v) sessionStorage.setItem('zoya-chat-draft', v)
  else sessionStorage.removeItem('zoya-chat-draft')
})
const sending = ref(false)
const loadingOlder = ref(false)
const hasMore = ref(false)
const initialized = ref(false)
const initializing = ref(false)

const scrollContainer = ref(null)
const inputEl = ref(null)
const inputBar = ref(null)
const inputBarHeight = ref(146) // До первого замера панели через ResizeObserver

// ── Keyboard detection (iOS + Android) ─────────────────
const keyboardVisible = ref(false)
const keyboardHeight = ref(0)

function onViewportResize() {
  const vv = window.visualViewport
  if (!vv) return
  // На iOS visualViewport.height уменьшается при открытии клавиатуры, а сам
  // вьюпорт вдобавок сдвигается — offsetTop. Панель ввода позиционируется
  // от нижнего края макетного вьюпорта, поэтому вычитаем оба слагаемых.
  const kbH = window.innerHeight - vv.height - vv.offsetTop
  keyboardVisible.value = kbH > 80
  keyboardHeight.value = kbH > 80 ? kbH : 0
}

// iOS открывает клавиатуру анимацией и по ходу двигает визуальный вьюпорт,
// причём сдвиг (offsetTop) приходит событием scroll, а не resize. Одной замерки
// по таймеру не хватало: она попадала в середину анимации, панель ввода
// оставалась ниже, чем нужно, и клавиатура наезжала на неё.
let keyboardTrackUntil = 0
let keyboardFrame = 0
function trackKeyboard(durationMs = 700) {
  const first = keyboardTrackUntil === 0
  keyboardTrackUntil = performance.now() + durationMs
  if (!first) return                      // цикл уже крутится, только продлили
  const step = () => {
    onViewportResize()
    if (performance.now() < keyboardTrackUntil) {
      keyboardFrame = requestAnimationFrame(step)
    } else {
      keyboardTrackUntil = 0
      scrollToBottom()
    }
  }
  keyboardFrame = requestAnimationFrame(step)
}

function onInputFocus() {
  trackKeyboard()
}

// ── Voice input: тот же рекордер, endpoint, модель и квоты, что у AddFoodDialog ──
const voiceSupported = !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== 'undefined'
const showVoiceSheet = ref(false)
const transcribing = ref(false)
const voiceError = ref('')
const voiceAbort = new AbortController()
watch([inputText, showVoiceSheet, transcribing], ([text, recording, pending]) => {
  if (text.trim() || recording || pending) markUnsaved(chatUnsavedKey)
  else clearUnsaved(chatUnsavedKey)
}, { immediate: true })

async function openVoice() {
  if (sending.value || transcribing.value) return
  if (authStore.quotaExhausted('msg')) { authStore.openFirstPaywall('chat'); return }
  if (!authStore.hasAiAccess) { authStore.showPaywall = true; return }
  if (voiceAbort.signal.aborted) return
  inputEl.value?.blur()
  voiceError.value = ''
  showVoiceSheet.value = true
}

async function onVoiceRecorded({ base64, mime }) {
  showVoiceSheet.value = false
  if (!base64) return
  transcribing.value = true
  voiceError.value = ''
  try {
    const { data } = await api.post('/ai/recognize-voice', {
      audio: base64, mime_type: mime, mode: 'transcript',
    }, { timeout: 90000, signal: voiceAbort.signal })
    const transcript = String(data?.result?.transcript || '').trim()
    if (!transcript) {
      voiceError.value = 'Не удалось расслышать запись. Попробуйте ещё раз — ближе к микрофону и без шума.'
      return
    }
    inputText.value = [inputText.value.trimEnd(), transcript].filter(Boolean).join(' ')
    await nextTick()
    autoResize()
  } catch (err) {
    if (voiceAbort.signal.aborted) return
    const code = err.response?.data?.error
    if (isAiAccessError(err)) return
    voiceError.value = typeof code === 'string' && /[а-яё]/i.test(code)
      ? code : 'Не удалось распознать речь. Проверьте подключение и попробуйте снова.'
  } finally {
    transcribing.value = false
  }
}

let inputBarObserver
function measureInputBar() {
  if (activeTab.value === 'chat' && inputBar.value) inputBarHeight.value = inputBar.value.offsetHeight
}
watch(activeTab, async (tab) => {
  if (tab !== 'chat') { inputEl.value?.blur(); return }
  void initChat()
  await nextTick()
  autoResize()
  measureInputBar()
})
watch([inputBarHeight, keyboardHeight], async () => {
  if (activeTab.value !== 'chat' || document.activeElement !== inputEl.value) return
  await nextTick()
  scrollToBottom(true)
})

// ── Add food from chat ───────────────────────────
const showTextRecognize = ref(false)
const textRecognizeItems = ref([])

function openAddFood(items) {
  textRecognizeItems.value = items.map(item => ({
    name: item.name,
    weight_g: item.weight_g || 100,
    // «2 яйца» из чата: количество и единица, граммы пересчитает окно по
    // весу штуки (utils/units.js), если у продукта она есть
    qty: item.qty > 0 ? Number(item.qty) : null,
    unit_hint: item.unit ? String(item.unit) : null,
    type: 'direct',
    cooking_method: 'raw',
    ingredients: [],
    product_id: null,
    matched: false,
  }))
  showTextRecognize.value = true
}

function onFoodAdded() {
  showTextRecognize.value = false
}

// ── Init ──────────────────────────────────────────
const sleep = ms => new Promise(r => setTimeout(r, ms))

onMounted(async () => {
  measureInputBar()
  inputBarObserver = new ResizeObserver(measureInputBar)
  inputBarObserver.observe(inputBar.value)
  onViewportResize()
  window.visualViewport?.addEventListener('resize', onViewportResize)
  window.visualViewport?.addEventListener('scroll', onViewportResize)
  // Подгоняем высоту textarea под восстановленный черновик
  if (inputText.value) nextTick(() => autoResize())

  if (activeTab.value === 'chat') await initChat()
})

// Вход в настройки не помечает сообщения прочитанными: чат загружается при открытии.
async function initChat() {
  if (!authStore.user || initialized.value || initializing.value) return
  initializing.value = true
  try {
    const { data } = await api.post('/chat/init')
    // Сервер в /chat/init снял отметку непрочитанного — гасим значок сразу
    authStore.markZoyaSeen()
    const msgs = data.messages || []
    hasMore.value = data.hasMore || false

    // Welcome: show typing animation only on first visit after onboarding
    const showWelcomeAnim = localStorage.getItem('zoya-show-welcome-animation')
    if (showWelcomeAnim && msgs.length && msgs.every(m => m.role === 'zoya')) {
      localStorage.removeItem('zoya-show-welcome-animation')
      initialized.value = true
      for (const msg of msgs) {
        sending.value = true
        await nextTick()
        scrollToBottom(true)
        await sleep(1200)
        sending.value = false
        messages.value.push(msg)
        await nextTick()
        scrollToBottom()
      }
    } else {
      messages.value = msgs
      initialized.value = true
      await nextTick()
      scrollToBottom(true)
    }
  } catch (err) {
    console.error('Chat init error:', err)
  } finally {
    initializing.value = false
  }
}

onUnmounted(() => {
  inputBarObserver?.disconnect()
  voiceAbort.abort()
  cancelAnimationFrame(keyboardFrame)
  window.visualViewport?.removeEventListener('resize', onViewportResize)
  window.visualViewport?.removeEventListener('scroll', onViewportResize)
})

// ── Send message ──────────────────────────────────
async function sendMessage() {
  const text = inputText.value.trim()
  if (!text || sending.value || transcribing.value) return

  if (!authStore.hasAiAccess) { authStore.showPaywall = true; return }
  // Бесплатные сообщения кончились — первый пейвол (lib/freeLimits.js)
  if (authStore.quotaExhausted('msg')) { authStore.openFirstPaywall('chat'); return }


  inputText.value = ''
  resetTextarea()
  sending.value = true

  // Optimistic: add user message locally
  const tempId = Date.now()
  messages.value.push({
    id: tempId,
    role: 'user',
    content: text,
    metadata: null,
    created_at: new Date().toISOString(),
  })
  await nextTick()
  scrollToBottom()

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const { data } = await api.post('/chat/send', { content: text, tz })
    authStore.bumpQuota('msg')
    track('chat_message_sent', { is_onboarding: !authStore.user?.onboarding_done })

    // Replace temp message with real one
    const idx = messages.value.findIndex(m => m.id === tempId)
    if (idx !== -1 && data.userMessage) {
      messages.value[idx] = data.userMessage
    }

    // Add Zoya reply
    if (data.message) {
      messages.value.push(data.message)
    }

    // Update token budget from response
    if (data.pct != null) authStore.updateTokenBudget(data)

    // Update auth store if onboarding changed user data
    authStore.checkAuthStatus()
  } catch (err) {
    if (isAiAccessError(err)) {
      messages.value = messages.value.filter(message => message.id !== tempId)
      if (!inputText.value) inputText.value = text
      await nextTick()
      autoResize()
      return
    }
    console.error('Send error:', err)
    // Add error message
    messages.value.push({
      id: Date.now(),
      role: 'zoya',
      content: 'Извини, произошла ошибка. Попробуй ещё раз.',
      metadata: null,
      created_at: new Date().toISOString(),
    })
  } finally {
    sending.value = false
    await nextTick()
    scrollToBottom()
  }
}

// Из вкладок плана и памяти — в чат с заготовкой сообщения (человек правит и шлёт сам)
async function askZoya(text) {
  activeTab.value = 'chat'
  inputText.value = text
  await nextTick()
  autoResize()
  measureInputBar()
  const el = inputEl.value
  el?.focus({ preventScroll: true })
  el?.setSelectionRange(text.length, text.length)
  if (el) el.scrollTop = el.scrollHeight
  scrollToBottom(true)
}

// Крестик у «Запомнила: …» — убрать факт из памяти (api/memory.js)
async function forgetFact(msg, f) {
  try {
    await api.delete(`/memory/${f.id}`)
    msg.metadata = { ...msg.metadata, remembered: (msg.metadata.remembered || []).filter(x => x.id !== f.id) }
  } catch (err) {
    console.error('Memory delete error:', err)
  }
}

// ── Send action (onboarding buttons) ──────────────
async function sendAction(action, label) {
  if (sending.value) return

  // Navigation actions — redirect without API call
  if (action && action.startsWith('navigate:')) {
    router.push(action.slice(9))
    return
  }

  sending.value = true

  // Optimistic: show what user clicked
  messages.value.push({
    id: Date.now(),
    role: 'user',
    content: label,
    metadata: { action },
    created_at: new Date().toISOString(),
  })
  await nextTick()
  scrollToBottom()

  try {
    // tz нужен и здесь: без него бэкенд считает сутки по UTC, и ночная еда
    // уезжает во вчерашний день (см. buildFullContext).
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const { data } = await api.post('/chat/send', { content: label, action, tz })

    // Add Zoya reply
    if (data.message) {
      messages.value.push(data.message)
    }

    // Update token budget from response
    if (data.pct != null) authStore.updateTokenBudget(data)

    authStore.checkAuthStatus()
  } catch (err) {
    console.error('Action error:', err)
  } finally {
    sending.value = false
    await nextTick()
    scrollToBottom()
  }
}

// ── Load older messages ───────────────────────────
async function loadOlder() {
  if (loadingOlder.value || !hasMore.value || messages.value.length === 0) return

  loadingOlder.value = true
  const oldestId = messages.value[0].id
  const scrollEl = scrollContainer.value
  const oldScrollHeight = scrollEl.scrollHeight

  try {
    const { data } = await api.get(`/chat/messages?before=${oldestId}&limit=30`)
    if (data.messages?.length) {
      messages.value.unshift(...data.messages)
      hasMore.value = data.hasMore
      await nextTick()
      // Preserve scroll position
      scrollEl.scrollTop = scrollEl.scrollHeight - oldScrollHeight
    } else {
      hasMore.value = false
    }
  } catch (err) {
    console.error('Load older error:', err)
  } finally {
    loadingOlder.value = false
  }
}

// ── Scroll detection ──────────────────────────────
function onScroll() {
  const el = scrollContainer.value
  if (!el) return
  if (el.scrollTop < 80 && hasMore.value && !loadingOlder.value) {
    loadOlder()
  }
}

// ── Scroll to bottom ──────────────────────────────
function scrollToBottom(instant = false) {
  const el = scrollContainer.value
  if (!el) return
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  el.scrollTo({ top: el.scrollHeight, behavior: instant || reducedMotion ? 'instant' : 'smooth' })
}

function onComposerEnter(event) {
  if (!event.isComposing) sendMessage()
}

// ── Textarea auto-resize ──────────────────────────
function autoResize() {
  const el = inputEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

function resetTextarea() {
  const el = inputEl.value
  if (!el) return
  el.style.height = '48px'
}

// ── Zoya message formatting (markdown-like) ──────
function formatZoyaMessage(text) {
  if (!text) return ''
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
  const fmtInline = s => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>')
  const lines = text.split('\n')
  let html = ''
  let listType = null

  for (const raw of lines) {
    const line = raw.trim()
    const bullet = line.match(/^[\*\-]\s+(.*)/)
    const numbered = line.match(/^(\d+)[\.\)]\s+(.*)/)
    const header = line.match(/^(#{1,3})\s+(.*)/)

    if (bullet || numbered) {
      const isBullet = !!bullet
      const tag = isBullet ? 'ul' : 'ol'
      const content = isBullet ? bullet[1] : numbered[2]
      if (listType !== tag) {
        if (listType) html += `</${listType}>`
        html += `<${tag} class="zf-list">`
        listType = tag
      }
      const valAttr = numbered ? ` value="${numbered[1]}"` : ''
      html += `<li${valAttr}>${fmtInline(esc(content))}</li>`
    } else {
      if (listType) { html += `</${listType}>`; listType = null }
      if (line === '') {
        if (html) html += '<div class="zf-spacer"></div>'
      } else if (header) {
        html += `<p class="zf-h${header[1].length}">${fmtInline(esc(header[2]))}</p>`
      } else {
        html += `<p>${fmtInline(esc(line))}</p>`
      }
    }
  }
  if (listType) html += `</${listType}>`
  return html
}

// ── Date / time formatting ────────────────────────
function formatTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

function formatDateSep(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diff = (today - msgDate) / (1000 * 60 * 60 * 24)

  if (diff === 0) return 'Сегодня'
  if (diff === 1) return 'Вчера'
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function showDateSep(idx) {
  if (idx === 0) return true
  const prev = new Date(messages.value[idx - 1].created_at)
  const curr = new Date(messages.value[idx].created_at)
  return prev.toDateString() !== curr.toDateString()
}
</script>

<style scoped>
.zoya-dialog { flex: none; min-height: 0; padding-bottom: 0; overflow: hidden; }
.chat-bottom-fade { background: linear-gradient(to top, var(--cream), transparent); }
.zoya-dialog button:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.zoya-bubble {
  font-family: Inter, sans-serif;
  font-size: var(--fs-15);
  line-height: 1.55;
  color: var(--ink);
}
.zoya-bubble :deep(p) {
  margin: 0 0 8px 0;
}
.zoya-bubble :deep(p:last-child) {
  margin-bottom: 0;
}
.zoya-bubble :deep(.zf-spacer) {
  height: 4px;
}
.zoya-bubble :deep(strong) {
  font-weight: 600;
}
.zoya-bubble :deep(em) {
  font-style: italic;
}
.zoya-bubble :deep(.zf-list) {
  margin: 4px 0 8px 0;
  padding-left: 20px;
  list-style: disc;
}
.zoya-bubble :deep(.zf-list li) {
  margin: 3px 0;
}
.zoya-bubble :deep(ol.zf-list) {
  list-style: decimal;
}
.zoya-bubble :deep(.zf-h1) {
  font-size: var(--fs-16);
  font-weight: 700;
  margin: 0 0 6px 0;
}
.zoya-bubble :deep(.zf-h2) {
  font-size: var(--fs-15);
  font-weight: 600;
  margin: 0 0 4px 0;
}
.zoya-bubble :deep(.zf-h3) {
  font-size: var(--fs-15);
  font-weight: 600;
  margin: 0 0 4px 0;
}

/* Перевёрнутые скругления у активного таба (как в Дневнике и Кулинарии) */
.tab-active::before,
.tab-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 14px;
  height: 14px;
  background: rgba(250, 243, 232, 0.75);
}
.tab-active::before {
  left: -14px;
  -webkit-mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
}
.tab-active::after {
  right: -14px;
  -webkit-mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
}
.tab-active.tab-first::before { display: none; }
.tab-active.tab-last::after { display: none; }
</style>
